import { Box, Paper, Stack, Tab, Tabs } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import ScheduleDefinitionEditor from "./SchedulingStrategyEditor/SchedulingStrategyEditor";
import RewardCalculationEditor from "./RewardCalculationEditor/RewardCalculationEditor";
import IncentivationSourceEditor from "./IncentivationSourceEditor/IncentivationSourceEditor";
import CommonActionModulePropertiesEditor from "./CommonActionModulePropertiesEditor";
import { GeneralButton } from "../../../common/ui/GeneralButton";
import { RewardType } from "../../../types/actionModule/rewardType";
import { ActionModuleEditorState } from "../../../types/actionModule/editor/actionModuleEditorTypes";
import useActionModule from "../../../hooks/actionModules/useActionModule";
import { PropertyType, PropertyTypes, SubtypeState } from "../../../types/actionModule/editor/subtypeEditorTypes";
import { subtypeDefaultProperties, subtypes } from "../../../types/actionModule/actionModuleTypes";
import { DefaultUnknownSubtypeDefinition } from "../../../types/actionModule/subtypes/defaultUnknownSubtypeDefinition";
import SubtypeEditor from "./SubtypeEditor/SubtypeEditor";
import { ActionModuleRequest } from "../../../types/actionModule/actionModule";
import { actionModulesApi } from "../../../api/actionsModulesApi";
import { BaseModuleSubtype } from "../../../types/actionModule/subtypes/subtype";
import { ActionModuleAssignmentTab } from "./ActionModuleAssignmentTab";
import { useTranslation } from "react-i18next";
import { Delete } from "@mui/icons-material";
import { useAppDispatch } from "../../../hooks/StoreHooks";
import { handleRequest } from "../../../store/utils/handleRequest";

interface Props {
    actionModuleId?: string;
    companyId: number;
    isNew: boolean;
    onSaved: () => void;
    onDeleted: () => void;
}

export default function ActionModuleEditor(props: Props) {
    const { t } = useTranslation();

    const dispatch = useAppDispatch();

    const [tabIndex, setTabIndex] = useState(0);
    const [isSaving, setIsSaving] = useState(false);
    const { isLoading, actionModule } = useActionModule(
        props.actionModuleId,
        props.companyId
    );

    const [editorState, setEditorState] = useState<ActionModuleEditorState>({
        subtypes: {
            subtypes: [],
            selectedSubtype: undefined,
        },
        schedulingStrategy: {
            selectedSchedulingStrategyType: null,
            schedulingStrategy: null,
        },
        incentivationSource: {
            selectedIncentivationSourceType: null,
            incentivationSource: null
        },
        rewardCalculation: {
            maxReward: 500,
            rewardType: RewardType.Points,
            rewardCalculationType: null,
            selectedRewardCalculationType: null,
        },
        actionModule: {
            id: 0,
            name: "",
            description: "",
            employeeDescription: "",
            maxReward: 0,
            rewardType: RewardType.Nothing,
            iconName: "",
            colorHex: "",
            positiveAnswerText: "",
            negativeAnswerText: "",
            iconUrl: null,
            maxUserCanComplete: null,
            detailedEmailNotification: false,
            summaryEmailNotification: false,
            emailNotificationAddresses: [],
            isCompanyWideAssigned: false,
            rewardCalculationType: null,
            completionDescriptionText: "",
            order: 0,
            assignedEmployeeUserIds: [],
            assignedGroupIds: [],
            companyId: props.companyId
        },
        isNew: props.isNew,
    });

    useEffect(() => {
        if (!actionModule) {
            return;
        }

        const subtypeStates: SubtypeState[] = actionModule.subtypes.map((s, i) => {
            const subtypeDefinition = subtypes.find((d) => d.type === s.subtype);
            const properties: Record<string, PropertyType> = {};

            const propertyDefinitions = [
                ...subtypeDefinition?.editableProperties ?? [],
                ...subtypeDefaultProperties,
            ]

            for (const property of propertyDefinitions) {
                const name = property.name;
                const value = s[name];

                switch (property.propertyType) {
                    case PropertyTypes.text:
                        if (value && typeof value !== "string") {
                            console.warn(`Expected string type, got ${typeof value} for subtype property ${property.name}`, value);
                        } else {
                            properties[name] = value ?? undefined
                        }
                        break;
                    case PropertyTypes.boolean:
                        if (value && typeof value !== "boolean") {
                            console.warn(`Expected boolean type, got ${typeof value} for subtype property ${property.name}`);
                        } else {
                            properties[name] = value ?? undefined
                        }
                        break;
                    case PropertyTypes.integer:
                    default:
                        if (value && typeof value !== "number") {
                            console.warn(`Expected integer type, got ${typeof value} for subtype property ${property.name}`);
                        } else {
                            properties[name] = value ?? undefined
                        }
                        break;
                    case PropertyTypes.complex:
                        if (!property.complexModelToEditorState) {
                            console.warn(`Subtype property ${property.name} does not have value mapping functions defined`);
                        } else {
                            properties[name] = property.complexModelToEditorState(value)
                        }
                        break;
                }
            }

            return {
                definition: subtypeDefinition ?? DefaultUnknownSubtypeDefinition,
                subtype: s,
                properties: properties,
                index: i,
                editorId: i,
            }
        })

        setEditorState({
            rewardCalculation: {
                rewardCalculationType: actionModule.rewardCalculationType,
                selectedRewardCalculationType: actionModule.rewardCalculationType?.type ?? null,
                maxReward: actionModule.maxReward,
                rewardType: actionModule.rewardType,
            },
            incentivationSource: {
                selectedIncentivationSourceType: actionModule.incentivationSource?.type ?? null,
                incentivationSource: actionModule.incentivationSource,
            },
            schedulingStrategy: {
                selectedSchedulingStrategyType: actionModule.schedulingStrategy?.type ?? null,
                schedulingStrategy: actionModule.schedulingStrategy,
            },
            subtypes: {
                subtypes: subtypeStates,
                selectedSubtype: undefined
            },
            actionModule: actionModule,
            isNew: false,
        })
    }, [actionModule])

    const onDelete = useCallback(async () => {
        if (!props.actionModuleId) {
            return;
        }

        setIsSaving(true);

        let success: boolean;

        const result = await handleRequest(
            dispatch,
            "direct",
            () => actionModulesApi.adminDeleteAction(props.companyId, props.actionModuleId!),
            t("actionModuleEditor.actionModuleDeletedSuccess"),
        );
        success = result.success();

        setIsSaving(false);

        if (success) {
            props.onDeleted();
        }
    }, [props.companyId, props.actionModuleId]);

    const onSave = useCallback(async () => {
        if (!editorState.actionModule) {
            return;
        }

        const subtypes: BaseModuleSubtype[] = editorState.subtypes.subtypes.map((s, i) => {
            const model: BaseModuleSubtype = {
                ...s.subtype,
                id: s.subtype.id,
                order: i,
                subtype: s.definition.type,
            };

            const properties = [...subtypeDefaultProperties, ...s.definition.editableProperties];

            for (const property of properties) {
                const editorStateValue = s.properties[property.name] ?? property.defaultValue;
                const modelValue = property.complexEditorStateToModel && editorStateValue
                    ? property.complexEditorStateToModel(editorStateValue)
                    : editorStateValue;

                model[property.name] = modelValue;
            }

            return model;
        })

        // Exclude id and iconUrl from the request, those properties are not editable
        const { id, iconUrl, ...actionModule } = { ...editorState.actionModule };

        const schedulingStrategy = editorState.schedulingStrategy.selectedSchedulingStrategyType
            ? {
                ...editorState.schedulingStrategy.schedulingStrategy,
                type: editorState.schedulingStrategy.selectedSchedulingStrategyType
            } : null;

        const rewardCalculationType = editorState.rewardCalculation.selectedRewardCalculationType
            ? {
                ...editorState.rewardCalculation.rewardCalculationType,
                type: editorState.rewardCalculation.selectedRewardCalculationType,
            } : null;

        const incentivationSource = editorState.incentivationSource.selectedIncentivationSourceType
            ? {
                ...editorState.incentivationSource.incentivationSource,
                type: editorState.incentivationSource.selectedIncentivationSourceType,
            } : null;

        const request: ActionModuleRequest = {
            ...actionModule,
            incentivationSource: incentivationSource,
            schedulingStrategy: schedulingStrategy,
            rewardCalculationType: rewardCalculationType,
            subtypes: subtypes,
            maxReward: editorState.rewardCalculation.maxReward,
        };

        setIsSaving(true);

        let success: boolean;

        if (editorState.isNew) {
            const result = await handleRequest(
                dispatch,
                "direct",
                () => actionModulesApi.adminCreateAction(props.companyId, request, false),
                t("actionModuleEditor.actionModuleCreatedSuccess"),
            );
            success = result.success();
        } else {
            const result = await handleRequest(
                dispatch,
                "direct",
                () => actionModulesApi.adminEditAction(id, props.companyId, request, false),
                t("actionModuleEditor.actionModuleSavedSuccess"),
            );
            success = result.success();
        }

        setIsSaving(false);

        if (success) {
            props.onSaved();
        }
    }, [editorState]);

    return <>
        <Stack direction="row">
            <Tabs style={{ flex: 1 }}
                value={tabIndex}
                onChange={(_, v) => setTabIndex(v)}
            >
                <Tab label={t("actionModuleEditor.tabGeneral.title")} />
                <Tab label={t("actionModuleEditor.tabSubtypes.title")} />
                <Tab label={t("actionModuleEditor.tabSchedulingStrategy.title")} />
                <Tab label={t("actionModuleEditor.tabRewardCalculation.title")} />
                <Tab label={t("actionModuleEditor.tabIncentivationSource.title")} />
                <Tab label={t("actionModuleEditor.tabModuleAssignment.title")} />
            </Tabs>
            {
                props.actionModuleId &&
                <GeneralButton
                    appVariant="dangerIcon"
                    loading={isLoading || isSaving}
                    sx={{ alignSelf: "center", mr: 1 }}
                    onClick={() => onDelete()}>
                    <Delete />
                </GeneralButton>
            }
            <GeneralButton
                appVariant="primary"
                loading={isLoading || isSaving}
                sx={{ alignSelf: "center" }}
                onClick={() => onSave()}>
                {t("buttons.save")}
            </GeneralButton>
        </Stack>
        <Paper sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            <CustomTabPanel value={tabIndex} index={0}>
                <CommonActionModulePropertiesEditor
                    editorState={editorState}
                    onChanged={(a) => setEditorState({
                        ...editorState,
                        actionModule: {
                            ...editorState.actionModule,
                            ...a
                        },
                    })} />
            </CustomTabPanel>
            <CustomTabPanel value={tabIndex} index={1}>
                <SubtypeEditor
                    value={editorState.subtypes}
                    onChanged={
                        (v) => setEditorState({
                            ...editorState,
                            subtypes: v
                        })}
                />
            </CustomTabPanel>
            <CustomTabPanel value={tabIndex} index={2}  >
                <Box sx={{ height: "100%", overflowY: "auto" }}>
                    <ScheduleDefinitionEditor
                        value={editorState.schedulingStrategy}
                        onChange={(v) => setEditorState({
                            ...editorState,
                            schedulingStrategy: v
                        })}
                    />
                </Box>
            </CustomTabPanel>
            <CustomTabPanel value={tabIndex} index={3}>
                <RewardCalculationEditor
                    value={editorState.rewardCalculation}
                    onChange={(v) => setEditorState({
                        ...editorState,
                        rewardCalculation: v,
                    })}
                />
            </CustomTabPanel>
            <CustomTabPanel value={tabIndex} index={4}>
                <IncentivationSourceEditor
                    value={editorState.incentivationSource}
                    onChange={(v) => setEditorState({
                        ...editorState,
                        incentivationSource: v
                    })}
                />
            </CustomTabPanel>
            <CustomTabPanel value={tabIndex} index={5}>
                <ActionModuleAssignmentTab
                    companyId={props.companyId}
                    value={editorState.actionModule}
                    onChanged={(v) => setEditorState({
                        ...editorState,
                        actionModule: v
                    })}
                />
            </CustomTabPanel>
        </Paper>
    </>
}

interface TabPanelProps {
    children: React.ReactNode;
    index: number;
    value: number;
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index } = props;

    if (value !== index) {
        return <></>;
    }

    return <>{children}</>;
}